import React, { useEffect, useMemo, useState } from 'react';
import SidebarToggleHandler from '../../../../../@coremat/CmtLayouts/Vertical/SidebarToggleHandler';
import Toolbar from '@mui/material/Toolbar';
import { Box, Typography, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import Logo from '../Logo';
import CartButton from '../CartButton/CartButton';
import { useLocation } from 'react-router';
import { NavLink, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IsImpersonationMode, IsLocalDevOrServerDev, IsProduction, homePage } from 'config/config';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SiteNav from '@acnb/CustomComponents/SiteNav/SiteNav';
import MegaSiteNav from '@acnb/CustomComponents/MegaSiteNav/MegaSiteNav';
import QuickDealerChangeModal from '@acnb/CustomComponents/DealerCard/QuickDealerChangeModal';
import {
  doesSectionUseMegaMenu,
  getDealerPortalSectionName,
  getDealerPortalSectionValue,
  DealerPortalSectionEnumValue,
} from 'enums/DealerPortalSectionEnum';
import { useBrpClubDealer } from 'services/Data/DealerServices';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import { useActiveCart } from 'services/Data/CartService';
import { useActiveExteriorSignageQuote } from 'services/Data/ExteriorSignageQuoteService';
import { useActiveMerchandisingProgramQuote } from 'services/Data/MerchandisingProgramQuoteService';
import { buildMenuItems } from 'services/Utilities/menuUtils';
import { shouldShowDealerSiteNav } from 'services/Utilities/dealerSiteNavUtils';
import {
  hasAnyActiveDealerWork,
  isBrpClubDealerSelectionTriggerRequested,
  removeBrpClubDealerSelectionTrigger,
} from 'services/Utilities/dealerWorkUtils';

const useStyles = makeStyles(theme => ({
  '@keyframes dealerBorderBreath': {
    '0%': {
      borderColor: alpha('#FFD200', 0.5),
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.16), 0 0 0 0 rgba(255, 210, 0, 0.16)',
    },
    '50%': {
      borderColor: alpha('#FFD200', 1),
      boxShadow: '0 10px 26px rgba(0, 0, 0, 0.22), 0 0 0 3px rgba(255, 210, 0, 0.26)',
    },
    '100%': {
      borderColor: alpha('#FFD200', 0.5),
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.16), 0 0 0 0 rgba(255, 210, 0, 0.16)',
    },
  },
  root: {
    width: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    minHeight: 64,
    [theme.breakpoints.up('md')]: {
      minHeight: 72,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: 24,
      paddingRight: 24,
    },
  },
  searchRoot: {
    position: 'relative',
    width: 260,
    [theme.breakpoints.up('md')]: {
      width: 350,
    },
    '& .MuiSvgIcon-root': {
      position: 'absolute',
      left: 18,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
    },
    '& .MuiInputBase-root': {
      width: '100%',
    },
    '& .MuiInputBase-input': {
      height: 48,
      borderRadius: 30,
      backgroundColor: alpha(theme.palette.common.dark, 0.38),
      color: alpha(theme.palette.common.white, 0.7),
      boxSizing: 'border-box',
      padding: '5px 15px 5px 50px',
      transition: 'all 0.3s ease',
      '&:focus': {
        backgroundColor: alpha(theme.palette.common.dark, 0.58),
        color: alpha(theme.palette.common.white, 0.7),
      },
    },
  },
  langRoot: {
    borderLeft: `solid 1px ${alpha(theme.palette.common.dark, 0.15)}`,
    minHeight: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 8,
    paddingRight: 8,
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      minHeight: 64,
    },
  },
  iconBtn: {
    color: alpha(theme.palette.common.white, 0.38),
    '&:hover, &:focus': {
      color: theme.palette.common.white,
    },
  },
  navContainer: {
    "& div[class^='src__GridItem-sc'] a": {
      width: '100%!important',
      height: '100%!important',
      alignContent: 'space-around',
      textAlign: 'center',
    },
  },
  dealerCard: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 10,
    borderRadius: 12,
    border: `1px solid ${alpha('#FFD200', 0.75)}`,
    background: `linear-gradient(135deg, ${alpha('#FFF7CC', 0.16)}, ${alpha('#FFFFFF', 0.06)})`,
    backdropFilter: 'blur(8px)',
    boxShadow: '0 10px 26px rgba(0, 0, 0, 0.2)',
    animation: '$dealerBorderBreath 2.3s ease-in-out infinite',
    padding: '8px 10px',
    margin: 'auto',
    maxWidth: 'none',
    width: 'fit-content',
    minWidth: 'fit-content',
    flexShrink: 0,
    overflow: 'visible',
  },
  dealerName: {
    color: theme.palette.common.white,
    fontWeight: 700,
    lineHeight: 1,
    fontSize: 13,
    whiteSpace: 'nowrap',
  },
  dealerNumber: {
    color: alpha(theme.palette.common.white, 0.92),
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 0',
    whiteSpace: 'nowrap',
  },
  dealerMeta: {
    color: alpha(theme.palette.common.white, 0.9),
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    backgroundColor: alpha(theme.palette.common.white, 0.14),
    border: `1px solid ${alpha(theme.palette.common.white, 0.26)}`,
    borderRadius: 999,
    padding: '4px 9px',
    whiteSpace: 'nowrap',
  },
  dealerPillsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 8,
    minWidth: 0,
    flex: 1,
    overflow: 'visible',
  },
  switchDealerBtn: {
    minWidth: 0,
    marginLeft: 2,
    whiteSpace: 'nowrap',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 13,
    color: '#111111',
    backgroundColor: '#FFD200',
    border: '1px solid #e0b900',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
    padding: '7px 12px',
    '&:hover': {
      backgroundColor: '#e8be00',
      borderColor: '#d2ab00',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.28)',
    },
  },
}));

const BrpClubHeader = () => {
  const classes = useStyles();
  const [isDealerSelectModalOpen, setIsDealerSelectModalOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const accountReducer = useSelector(state => state.AccountReducer);
  const isLoggedIn = Boolean(accountReducer?.isLoggedIn);
  const dealerId = accountReducer?.profile?.dealer_Id;
  const { data: dealer } = useBrpClubDealer(accountReducer?.profile?.temporaryDealer_Id);
  const { data: activeCart, isLoading: isLoadingActiveCart } = useActiveCart(
    isLoggedIn,
    false,
    IsImpersonationMode ? dealerId : null,
  );
  const {
    data: activeMerchandisingProgramQuote,
    isLoading: isLoadingActiveMerchandisingProgramQuote,
  } = useActiveMerchandisingProgramQuote(isLoggedIn);
  const { data: activeExteriorSignageQuote, isLoading: isLoadingActiveExteriorSignageQuote } = useActiveExteriorSignageQuote(
    isLoggedIn,
  );

  const dealerName = dealer?.customerName || t('dealer');
  const dealerNumber = dealer?.identifier;
  const dealerPhone = dealer?.phoneNumber;
  const dealerLocation = [dealer?.city, dealer?.region?.internalName, dealer?.postalCode].filter(Boolean).join(', ');

  const theme = useTheme();

  const showSiteNav = useMemo(() => shouldShowDealerSiteNav(location.pathname), [location.pathname]);
  const shouldHandleDealerSelectionTrigger = useMemo(() => {
    return isLoggedIn && location.pathname === homePage && isBrpClubDealerSelectionTriggerRequested(location.search);
  }, [isLoggedIn, location.pathname, location.search]);
  const isLoadingActiveDealerWork = useMemo(
    () => isLoadingActiveCart || isLoadingActiveMerchandisingProgramQuote || isLoadingActiveExteriorSignageQuote,
    [isLoadingActiveCart, isLoadingActiveExteriorSignageQuote, isLoadingActiveMerchandisingProgramQuote],
  );

  useEffect(() => {
    if (!shouldHandleDealerSelectionTrigger || isLoadingActiveDealerWork) {
      return;
    }

    const nextSearch = removeBrpClubDealerSelectionTrigger(location.search);
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      hash: location.hash,
    });

    if (
      !hasAnyActiveDealerWork({
        cart: activeCart,
        merchandisingProgramQuote: activeMerchandisingProgramQuote,
        exteriorSignageQuote: activeExteriorSignageQuote,
      })
    ) {
      setIsDealerSelectModalOpen(true);
    }
  }, [
    activeCart,
    activeExteriorSignageQuote,
    activeMerchandisingProgramQuote,
    history,
    isLoadingActiveDealerWork,
    location.hash,
    location.pathname,
    location.search,
    shouldHandleDealerSelectionTrigger,
  ]);

  const currentSection = useMemo(() => {
    const pathnameSegments = location.pathname.split('/').filter(Boolean);
    const sectionSegmentIndex = pathnameSegments.findIndex(segment => segment.toLowerCase() === 'section');
    const sectionFromPath = getDealerPortalSectionValue(pathnameSegments[sectionSegmentIndex + 1]);

    if (sectionFromPath > DealerPortalSectionEnumValue.None) {
      return sectionFromPath;
    }

    if (location.pathname.startsWith('/dealer/useful-links')) {
      return DealerPortalSectionEnumValue.UsefulLinks;
    }

    if (location.pathname.startsWith('/dealer/home-page/exterior-signage')) {
      return DealerPortalSectionEnumValue.ExteriorSignage;
    }

    if (location.pathname.startsWith('/dealer/home-page/merchandising-program')) {
      return DealerPortalSectionEnumValue.MerchandisingServices;
    }

    return DealerPortalSectionEnumValue.DisplaysToolingAndFurniture;
  }, [location.pathname]);

  const currentSectionName = useMemo(() => getDealerPortalSectionName(currentSection) || 'DisplaysToolingAndFurniture', [
    currentSection,
  ]);
  const isMegaMenuSection = useMemo(() => doesSectionUseMegaMenu(currentSection), [currentSection]);

  const { data: dealerPortalMenuTreeData } = useDealerPortalMenuTree(
    currentSection,
    isLoggedIn && showSiteNav && !location.pathname.startsWith('/dealer/certification'),
  );

  const menuItems = useMemo(
    () =>
      buildMenuItems({
        pathname: location.pathname,
        t,
        dealerPortalMenuTreeData,
        language: i18n.language,
        currentSectionName,
      }),
    [location.pathname, t, dealerPortalMenuTreeData, i18n.language, currentSectionName],
  );

  return (
    <>
      <Toolbar className={classes.root}>
        <SidebarToggleHandler edge="start" color="inherit" aria-label="menu" />
        <Logo ml={2} color="white" />
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <NavLink to={homePage}>
            <Typography
              variant="h1"
              sx={{
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                marginLeft: 5,
                fontSize: { xs: 14, md: 21 },
              }}>
              {t('applicationHeaderName')}
            </Typography>
          </NavLink>
          <Box className={classes.dealerCard}>
            <Box className={classes.dealerPillsRow}>
              <Typography className={classes.dealerName}>{dealerName}</Typography>
              <Typography className={classes.dealerNumber}>
                {dealerNumber ? `#${dealerNumber}` : t('verifyDealer')}
              </Typography>
              {dealerLocation ? (
                <Typography className={classes.dealerMeta}>
                  {t('address')}: {dealerLocation}
                </Typography>
              ) : null}
              {dealerPhone ? (
                <Typography className={classes.dealerMeta} sx={{ flexShrink: 0 }}>
                  {t('phone')}: {dealerPhone}
                </Typography>
              ) : null}
            </Box>
            <Button className={classes.switchDealerBtn} onClick={() => setIsDealerSelectModalOpen(true)}>
              {t('notYourDealer')}
            </Button>
          </Box>
        </Box>
        <Box className={classes.langRoot}>
          <CartButton />

          {(IsLocalDevOrServerDev || (IsImpersonationMode && IsProduction)) && <LanguageSwitcher />}
        </Box>
      </Toolbar>
      <QuickDealerChangeModal show={isDealerSelectModalOpen} onClose={() => setIsDealerSelectModalOpen(false)} />
      {showSiteNav && (
        <div className={classes.navContainer}>
          {isMegaMenuSection ? (
            <MegaSiteNav
              items={menuItems}
              align="center"
              rowHeight={50}
              background={theme.palette.primary.dark}
              color={theme.palette.primary.contrastText}
              contentBackground={theme.palette.primary.dark}
              contentColor={theme.palette.primary.contrastText}
              breakpoint={768}
              style={{ zIndex: 99 }}
            />
          ) : (
            <SiteNav
              items={menuItems}
              align="center"
              rowHeight={50}
              background={theme.palette.primary.dark}
              color={theme.palette.primary.contrastText}
              contentBackground={theme.palette.primary.dark}
              contentColor={theme.palette.primary.contrastText}
              breakpoint={768}
              style={{ zIndex: 99 }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default BrpClubHeader;
